'use client';
import { cx } from 'instantsearch-ui-components';
import React from 'react';

export type ToggleRefinementClassNames = {
  /**
   * Class names to apply to the root element
   */
  root: string;
  /**
   * Class names to apply to the button element
   */
  button: string;
  /**
   * Class names to apply to the label text
   */
  labelText: string;
};

export type ToggleRefinementProps = Omit<React.ComponentProps<'div'>, 'onChange'> & {
  classNames?: Partial<ToggleRefinementClassNames>;
  onChange: (isChecked: boolean) => void;
  label: React.ReactNode;
  checked?: boolean;
  active?: boolean;
};

/**
 * ToggleRefinement component that renders as a button instead of a checkbox.
 * The component now has built-in styling that matches the design used in ClientSearchPageContent.
 * @example
 * ```jsx
 * // Usage with minimal styling (uses built-in defaults):
 * <ToggleRefinement
 *   attribute="productRange"
 *   on="Handrails"
 *   label={<><Icon name="Search" size={20} />All</>}
 * />
 *
 * // Usage with custom styling:
 * <ToggleRefinement
 *   attribute="productRange"
 *   on="Handrails"
 *   classNames={{
 *     button: 'custom-button-styles',
 *     labelText: 'custom-label-styles'
 *   }}
 *   label={<><Icon name="Search" size={20} />All</>}
 * />
 * ```
 */
export function ToggleRefinement({
  classNames = {},
  checked,
  active,
  onChange,
  label,
  ...props
}: ToggleRefinementProps) {
  const isActive = active ?? checked;

  return (
    <div {...props} className={cx('ais-ToggleRefinement', classNames.root, props.className)}>
      <button
        type="button"
        className={cx(
          'ais-ToggleRefinement-button',
          classNames.button,
          // Default styles that match the ClientSearchPageContent usage
          'cursor-pointer w-full flex items-center gap-xs hover:text-primary hover:underline font-semibold focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded',
          isActive ? 'text-primary' : 'text-secondary-400',
        )}
        onClick={() => {
          onChange(!isActive);
        }}
        aria-pressed={isActive}
      >
        <span
          className={cx(
            'ais-ToggleRefinement-labelText',
            classNames.labelText,
            'flex-1 text-left inline-flex items-center gap-xs',
          )}
        >
          {label}
        </span>
      </button>
    </div>
  );
}
